import React from 'react'
import { Typography, Checkbox, FormControlLabel } from '@material-ui/core'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import { StepperRow, commonValue as common } from './panelControls'

// Size and bar-fill controls for one or many fields of a type. `targets` is
// [{ placement }]; `onPatch(fn)` applies fn(placement) -> patch to every target.

// Width and height of a text, bar or color box.
export function BoxSizeControls({ targets, onPatch }) {
  const first = targets[0].placement
  const w = common(targets, (t) => t.placement.w ?? 20)
  const h = common(targets, (t) => t.placement.h ?? 4)
  return (
    <>
      <StepperRow
        label='W' value={w ?? (first.w ?? 20)} mixed={w === undefined} suffix='%' step={0.5} precision={2}
        onChange={(v) => onPatch(() => ({ w: Math.max(0, v) }))}
      />
      <StepperRow
        label='H' value={h ?? (first.h ?? 4)} mixed={h === undefined} suffix='%' step={0.5} precision={2}
        onChange={(v) => onPatch(() => ({ h: Math.max(0, v) }))}
      />
    </>
  )
}

export function BarFillControl({ targets, onPatch }) {
  const fill = common(targets, (t) => t.placement.hAlign ?? 'left')
  return (
    <div style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant='caption' color='textSecondary' style={{ minWidth: 60 }}>Fill from</Typography>
      <ToggleButtonGroup
        value={fill ?? null}
        exclusive
        size='small'
        onChange={(_, v) => v && onPatch(() => ({ hAlign: v }))}
      >
        <ToggleButton value='left' style={{ fontSize: 11, padding: '4px 8px', textTransform: 'none' }}>Left</ToggleButton>
        <ToggleButton value='right' style={{ fontSize: 11, padding: '4px 8px', textTransform: 'none' }}>Right</ToggleButton>
      </ToggleButtonGroup>
    </div>
  )
}

// Aspect lock plus width and height of an image. With the lock on, changing one side
// carries the other along at that image's own ratio.
export function ImageSizeControls({ targets, isLocked, onLockChange, onPatch }) {
  const ids = targets.map((t) => t.placement.fieldId)
  const lockedCount = ids.filter(isLocked).length
  const allLocked = lockedCount === ids.length
  const first = targets[0].placement
  // Height is shown as % of WIDTH (same unit as W) so equal W/H reads as a square box.
  // Stored as % of canvas height, so it converts on display (×1080/1920) and on edit.
  const shownHeight = (p) => +((p.imageHeight ?? 8) * 1080 / 1920).toFixed(2)
  const width = common(targets, (t) => t.placement.imageWidth ?? 8)
  const height = common(targets, (t) => shownHeight(t.placement))

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={allLocked}
            indeterminate={lockedCount > 0 && !allLocked}
            onChange={() => onLockChange(ids, !allLocked)}
            size='small'
            icon={<LinkOffIcon style={{ fontSize: 16 }} />}
            checkedIcon={<LinkIcon style={{ fontSize: 16 }} />}
            indeterminateIcon={<LinkIcon style={{ fontSize: 16, opacity: 0.5 }} />}
          />
        }
        label={<Typography variant='caption'>Lock aspect ratio</Typography>}
        style={{ margin: '0 0 0 4px' }}
      />
      <StepperRow
        label='W'
        value={width ?? (first.imageWidth ?? 8)}
        mixed={width === undefined}
        suffix='%'
        step={0.5}
        precision={2}
        onChange={(v) => {
          const w = Math.max(0, v)
          onPatch((p) => {
            if (!isLocked(p.fieldId)) return { imageWidth: w }
            const curW = p.imageWidth ?? 8
            const curH = p.imageHeight ?? 8
            const ratio = curW > 0 ? curH / curW : 1
            return { imageWidth: w, imageHeight: +(w * ratio).toFixed(2) }
          })
        }}
      />
      <StepperRow
        label='H'
        value={height ?? shownHeight(first)}
        mixed={height === undefined}
        suffix='%'
        step={0.5}
        precision={2}
        onChange={(v) => {
          const h = Math.max(0, v * 1920 / 1080)
          onPatch((p) => {
            if (!isLocked(p.fieldId)) return { imageHeight: +h.toFixed(2) }
            const curW = p.imageWidth ?? 8
            const curH = p.imageHeight ?? 8
            const ratio = curH > 0 ? curW / curH : 1
            return { imageHeight: +h.toFixed(2), imageWidth: +(h * ratio).toFixed(2) }
          })
        }}
      />
    </>
  )
}
